import { filterTools } from '@utils/tools/filterTools';
import { resolveTaxHouseholdReadOnly } from './resolveTaxHouseholdReadOnly';
import { SSAP_DATA_INCONSISTENCY_RULE_IDS } from './ssapDataInconsistencyConfig';

const getMemberLabel = member => {
    const { firstName, lastName } = member.name || {};
    const name = [ firstName, lastName ].filter(Boolean).join(' ').trim();
    return name || `personId ${member.personId}`;
};

export const hasIncomeAmountWithoutFrequency = income => {
    if (!income || income.amount === null || income.amount === '') {
        return false;
    }

    const amount = Number(income.amount);

    if (Number.isNaN(amount) || amount <= 0) {
        return false;
    }

    return income.frequency === null || income.frequency === '';
};

export const getRealIncomes = member =>
    (member.incomes || []).filter(filterTools.removeDummyType);

export const hasRealIncomeData = member => getRealIncomes(member).length > 0;

export const hasExpenseData = member => (member.expenses || []).length > 0;

const isPopulatedBoolean = value => value === true || value === false;

export const getHhmIncomeParamsIncomeMismatch = member => {
    if (!hasRealIncomeData(member)) {
        return null;
    }

    const { hhmIncomeParams } = member;

    if (!hhmIncomeParams) {
        return {
            field: 'hhmIncomeParams',
            detail: 'hhmIncomeParams is missing while income entries are present.',
        };
    }

    if (!isPopulatedBoolean(hhmIncomeParams.hasAnyIncome)) {
        return {
            field: 'hhmIncomeParams.hasAnyIncome',
            detail: `hasAnyIncome is ${hhmIncomeParams.hasAnyIncome} but non-dummy income entries exist.`,
        };
    }

    if (hhmIncomeParams.hasAnyIncome === false) {
        return {
            field: 'hhmIncomeParams.hasAnyIncome',
            detail: 'hasAnyIncome is false but non-dummy income entries exist.',
        };
    }

    return null;
};

export const getHhmIncomeParamsDeductionMismatch = member => {
    if (!hasExpenseData(member)) {
        return null;
    }

    const { hhmIncomeParams } = member;

    if (!hhmIncomeParams) {
        return {
            field: 'hhmIncomeParams',
            detail: 'hhmIncomeParams is missing while expense entries are present.',
        };
    }

    if (!isPopulatedBoolean(hhmIncomeParams.hasAnyDeduction)) {
        return {
            field: 'hhmIncomeParams.hasAnyDeduction',
            detail: `hasAnyDeduction is ${hhmIncomeParams.hasAnyDeduction} but expense entries exist.`,
        };
    }

    if (hhmIncomeParams.hasAnyDeduction === false) {
        return {
            field: 'hhmIncomeParams.hasAnyDeduction',
            detail: 'hasAnyDeduction is false but expense entries exist.',
        };
    }

    return null;
};

const incomeAmountWithoutFrequencyRule = {
    id: SSAP_DATA_INCONSISTENCY_RULE_IDS.INCOME_AMOUNT_WITHOUT_FREQUENCY,
    title: 'Income amount without frequency',
    description:
        'A household member has a non-dummy income with a positive amount but no pay frequency (frequency is null or empty).',
    run(data, reportIssue) {
        const taxHousehold = resolveTaxHouseholdReadOnly(data);

        if (!taxHousehold) {
            return;
        }

        taxHousehold.householdMember.forEach((member, memberIndex) => {
            (member.incomes || []).forEach((income, incomeIndex) => {
                if (!filterTools.removeDummyType(income) || !hasIncomeAmountWithoutFrequency(income)) {
                    return;
                }

                const memberLabel = getMemberLabel(member);
                const nonDummyOrdinal = getRealIncomes(member)
                    .findIndex(realIncome => realIncome === income) + 1;

                reportIssue({
                    ruleId: this.id,
                    title: this.title,
                    description: this.description,
                    message:
                        `${memberLabel} (personId ${member.personId}): non-dummy income #${nonDummyOrdinal} `
                        + `(incomes[${incomeIndex}]) has amount ${income.amount} but frequency is missing.`,
                    path: [
                        'taxHousehold',
                        'householdMember',
                        memberIndex,
                        'incomes',
                        incomeIndex,
                    ],
                    context: {
                        personId: member.personId,
                        memberLabel,
                        memberIndex,
                        incomeIndex,
                        amount: income.amount,
                        frequency: income.frequency,
                        sourceName: income.sourceName,
                        type: income.type,
                        subType: income.subType,
                    },
                });
            });
        });
    },
};

const hhmIncomeParamsNotPopulatedRule = {
    id: SSAP_DATA_INCONSISTENCY_RULE_IDS.HHM_INCOME_PARAMS_NOT_POPULATED,
    title: 'HHM income params not aligned with income or deduction data',
    description:
        'When a household member has income or deduction entries, hhmIncomeParams must be set '
        + '(hasAnyIncome / hasAnyDeduction must be true or false, not null).',
    run(data, reportIssue) {
        const taxHousehold = resolveTaxHouseholdReadOnly(data);

        if (!taxHousehold) {
            return;
        }

        taxHousehold.householdMember.forEach((member, memberIndex) => {
            const mismatches = [
                getHhmIncomeParamsIncomeMismatch(member),
                getHhmIncomeParamsDeductionMismatch(member),
            ].filter(Boolean);

            if (mismatches.length === 0) {
                return;
            }

            const memberLabel = getMemberLabel(member);

            mismatches.forEach(mismatch => {
                reportIssue({
                    ruleId: this.id,
                    title: this.title,
                    description: this.description,
                    message:
                        `${memberLabel} (personId ${member.personId}): ${mismatch.detail}`,
                    path: [
                        'taxHousehold',
                        'householdMember',
                        memberIndex,
                        mismatch.field,
                    ],
                    context: {
                        personId: member.personId,
                        memberLabel,
                        memberIndex,
                        field: mismatch.field,
                        hhmIncomeParams: member.hhmIncomeParams ?? null,
                        incomeCount: getRealIncomes(member).length,
                        expenseCount: (member.expenses || []).length,
                    },
                });
            });
        });
    },
};

/** @type {Record<string, typeof incomeAmountWithoutFrequencyRule>} */
export const SSAP_DATA_INCONSISTENCY_RULES_BY_ID = {
    [incomeAmountWithoutFrequencyRule.id]: incomeAmountWithoutFrequencyRule,
    [hhmIncomeParamsNotPopulatedRule.id]: hhmIncomeParamsNotPopulatedRule,
};
